import type { CampaignRequestAgent, RequestAgentKind } from "@/hooks/api/use-campaign-requests";

import { languageLabel } from "../constants";

/**
 * How an agent is named and tagged on the client's own request page.
 *
 * Two problems, one cause. An agent's nickname defaults to its language, so the badge on a market
 * read "English · English"; and the choice the client made is stored as a kind whose written label
 * is a whole clause — "Same script, translated" — which is fine as the option on a card in the
 * wizard and wrong as a pill sitting beside the word it repeats.
 */

/** The kind as a pill: one word wherever one word will do, never a clause. */
const AGENT_KIND_TAGS: Record<RequestAgentKind, string> = {
	agent_setup: "New build",
	reuse_existing: "Reused",
	regional_deployment: "New market",
	translated_agent: "Translated",
	agent_revision: "Revised",
};

export function agentKindTag(kind: RequestAgentKind | null | undefined): string | null {
	return kind ? (AGENT_KIND_TAGS[kind] ?? null) : null;
}

/**
 * What the wizard puts between a campaign's name and the language when it names an agent for the
 * client. Exported because the wizard writes the name and this reads it back: two copies of this
 * string are two answers to whether a client named their own agent.
 */
export const AGENT_NAME_SEPARATOR = " — ";

/**
 * Whether a name is still one of ours rather than one the client typed.
 *
 * Two defaults, not one. An agent on a campaign with no title is named for its language alone; on
 * a titled campaign it is `Q4 Renewals — English`, which is still ours rather than theirs. On the
 * `===` test by itself the second one reads as a name the client chose, which is every agent on
 * every titled campaign that kept the default — so the badge went back to saying
 * "Q4 Renewals — English · English", which is the thing this file exists to stop.
 *
 * The wizard asks the same question of a label it is about to rewrite when the campaign is
 * renamed, which is why this takes the two fields rather than an agent: one answer, not two.
 */
export function isDefaultAgentName(label: string | null | undefined, language: string): boolean {
	const value = (label ?? "").trim().toLowerCase();
	if (value.length === 0) return true;

	const spoken = languageLabel(language).toLowerCase();
	return value === spoken || value.endsWith(`${AGENT_NAME_SEPARATOR}${spoken}`);
}

function isNamed(agent: CampaignRequestAgent): boolean {
	return !isDefaultAgentName(agent.label, agent.language);
}

/**
 * What the agent is called at the top of its own card: the nickname where there is one, and the
 * language where the nickname *is* the language. Never both.
 */
export function agentHeading(agent: CampaignRequestAgent): string {
	return isNamed(agent) ? agent.label.trim() : languageLabel(agent.language);
}

/** The language under that heading, dropped when the heading is already the language. */
export function agentLanguageLine(agent: CampaignRequestAgent): string | null {
	return isNamed(agent) ? languageLabel(agent.language) : null;
}

/**
 * The agent as `MarketScheduleCard` prints it.
 *
 * That card draws `label · language` and collapses to the language alone when the two are the
 * same string, so handing it a blank label is how an unnamed agent gets the one word it should
 * have. The card's own comparison is exact, which leaves `english`, and the titled default this
 * file exists for; both arrive here already decided.
 */
export function agentForMarketCard(agent: CampaignRequestAgent): CampaignRequestAgent {
	return isNamed(agent) ? agent : { ...agent, label: "" };
}
